import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { useAuth0 } from "@auth0/auth0-react";
import { Empty, Spin, Progress } from "antd";
import _ from "lodash";
import isEmpty from "lodash/isEmpty";
import toLower from "lodash/toLower";
import State from "../../components/dashboard/state/state";
import PieChart from "../../components/dashboard/pie/pieChart";
import IacType from "../../components/dashboard/iacType/iacType";
import Insight from "../../components/dashboard/insight/insight";
import Integration from "../../components/dashboard/integration/integration";
import {
  getClassifications,
  getTotalCost,
} from "../../redux/actions/classificationsActions";
import { getDashboardInsights } from "../../redux/actions/insightsActions";
import {
  getDashboardBaseAggregations,
  getDashboardFireflyAggregations,
  setOnboardingComplete,
  setOnlyProd,
  setProdAccounts,
  getAccountTotalAssets,
} from "../../redux/actions/dashboardActions";
import { getAccountIntegrations } from "../../redux/actions/onbordingActions";
import { getIacStacksBaseAggs } from "../../redux/actions/iacStacksActions";
import Loading from "../../shared/loading/loading";
import { formatIacStatus, numberWithCommas } from "../../utils/formatting";
import { providers } from "../../utils/providers";
import { emptyIacScreenFilters } from "../../consts/iacStacks";
import TopFiveProperties from "../../components/dashboard/topFiveProperties/topFiveProperties";
import Top5UnmanagedResources from "../../components/dashboard/top5UnmanagedResources/top5UnmanagedResources";
import { git_integrations } from "../integrationsCatalog/integrations_index";
import ProdToggle from "../../shared/prodToggle/prodToggle";
import ProgressBar from "../../shared/progressBar/progressBar";

import MissingIntegrationsModal from "../../components/dashboard/missingIntegrationsModal/missingIntegrationsModal";
import { emptyInventoryScreenFilters } from "../../consts/inventory";
import { getMissingIntegrationsValue } from "./utils";
import {CLOUD_PROVIDERS} from "../../consts/general";
import CloudWorkflows from "../../components/dashboard/cloudWorkflows/cloudWorkflows";

import "./dashboard.scss";

const Dashboard = () => {
  const { user } = useAuth0();
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [datesData, setDatesData] = useState([]);
  const [regionsData, setRegionsData] = useState([]);
  const [showRegions, setShowRegions] = useState(false);
  const [managedGraphData, setManagedGraphData] = useState([]);
  const [showManagedGraph, setShowManagedGraph] = useState(false);
  const [insightsData, setInsightsData] = useState({});
  const [totalAssetsCount, setTotalAssetsCount] = useState(0);
  const [statesData, setStatesData] = useState({
    Drifted: { total: 0, percentage: 0 },
    Codified: { total: 0, percentage: 0 },
    Ghost: { total: 0, percentage: 0 },
    Unmanaged: { total: 0, percentage: 0 },
  });
  const [formattedGraphData, setFormattedGraphData] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [sortedSteps, setSortedSteps] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [missingIntegrationsModalData, setMissingIntegrationsModalData] =
    useState(null);
  const [missingIntegrationsModalOpen, setMissingIntegrationsModalOpen] =
    useState(false);

  const iacTypes = ["terraform", "helm", "cloudformation", "kustomize", "pulumi", "k8s"];

  const aggregationsIsManaged = useSelector(
    (state) => state.dashboardReducer.aggregationsIsManaged
  );

  const aggregationsAssetsVolume = useSelector(
    (state) => state.dashboardReducer.aggregationsAssetsVolume
  );

  const aggregationsRegion = useSelector(
    (state) => state.dashboardReducer.aggregationsRegion
  );

  const aggregationsClassificaition = useSelector(
    (state) => state.dashboardReducer.aggregationsClassificaition
  );

  const classifications = useSelector(
    (state) => state.classificationsReducer.classifications
  );

  const totalCost = useSelector(
    (state) => state.classificationsReducer.totalCost
  );

  const providerIdByProviderType = useSelector(
    (state) => state.dashboardReducer.providerIdByProviderType
  ) || [];

  const onlyProd = useSelector((state) => state.dashboardReducer.onlyProd);

  const prodAccountNumbers = useSelector(
    (state) => state.dashboardReducer.prodAccountNumbers
  );

  const accountIntegrations = useSelector(
    (state) => state.onbordingReducer.integrations
  );

  const excludeMissingIntegrations = useSelector(
    (state) => state.onbordingReducer.account.disableFeatures?.excludeMissingIntegrations
  ) || [];

  const aggregationsMissingAccountNumbers = useSelector(
    (state) => state.iacStacksReducer.aggregationsMissingAccountNumbers
  ) || {};

  const aggregationsStateTypeStatus = useSelector(
    (state) => state.iacStacksReducer.aggregationsStateTypeStatus
  );

  const accountTotals = useSelector(
    (state) => state.dashboardReducer.accountTotals
  );

  const onbordingSteps = [
    {
      text: "Data sources",
      key: "provider",
    },
    {
      text: "IaC Tools",
      key: "iac",
    },
    {
      text: "GitOps",
      key: "gitOps",
    },
    {
      text: "ChatOps",
      key: "notifications",
    },
    {
      text: "Done",
      key: "done",
    },
  ];

  const dispatch = useDispatch();

  useEffect(() => {
    if (initLoading) {
      fetchInitData();
    } else {
      fetchData();
    }
  }, [onlyProd]);

  useEffect(() => {
    if (!initLoading && !loading) {
      handleSetProgress();
      handleGetManagedNumbers();
      getNumbersForCounters();
      handleArrangeRegion();
      handleArrangeClassifications();
      handleSetProdAccount();
    }
  }, [initLoading, loading]);

  useEffect(() => {
    if (totalAssetsCount > 0) {
      let statesData = {
        Drifted: { total: 0, percentage: 0 },
        Codified: { total: 0, percentage: 0 },
        Ghost: { total: 0, percentage: 0 },
        Unmanaged: { total: 0, percentage: 0 },
      };
      _.map(managedGraphData, (stateObj) => {
        if (_.keys(statesData)?.includes(stateObj?.key)) {
          statesData[stateObj?.key]["total"] = stateObj?.doc_count;
          statesData[stateObj?.key]["percentage"] = _.round(
            (stateObj?.doc_count / totalAssetsCount) * 100,
            2
          ).toFixed(2);
        }
      });
      setStatesData(statesData);
    }
  }, [totalAssetsCount]);

  const handleSetProgress = async () => {
    if (!isEmpty(accountIntegrations)) {
      let integrations = accountIntegrations;
      // remove provider integrations
      integrations = _.mapKeys(integrations, (val, integKey) => {
        if (!_.keys(providers)?.includes(integKey)) {
          return integKey;
        }
      });
      // group by sections
      const gitOpsTypes = _.map(git_integrations, (integ) => integ?.name);
      let groupIntegrations = {
        gitOps: false,
        notifications: false,
        done: false,
      };
      integrations = _.mapKeys(integrations, (val, integKey) => {
        if (integKey === "notifications" && !isEmpty(val)) {
          groupIntegrations["notifications"] = true;
        }
        if (
          (gitOpsTypes?.includes(integKey) || integKey === "git") &&
          !isEmpty(val)
        ) {
          groupIntegrations["gitOps"] = true;
        }
      });
      // sort steps by active or inactive
      let sortedGroupIntegrations = [
        { key: "gitOps", active: groupIntegrations["gitOps"] },
        { key: "notifications", active: groupIntegrations["notifications"] },
        { key: "done", active: false },
      ];
      if (_.some(_.values(groupIntegrations), (val) => !val)) {
        let sorted = [];
        _.mapValues(groupIntegrations, (val, key) => {
          if (val) {
            return sorted?.unshift({ key, active: val });
          }
          sorted?.push({ key, active: val });
        });
        sortedGroupIntegrations = sorted;
      }
      sortedGroupIntegrations?.unshift([
        { key: "provider", active: true },
        { key: "iac", active: true },
      ]);
      sortedGroupIntegrations = _.flatten(sortedGroupIntegrations);
      // add titles
      const sortedSteps = _.map(sortedGroupIntegrations, (step) => {
        const match = onbordingSteps?.find((st) => st?.key === step?.key);
        return { ...step, text: match ? match?.text : "" };
      });
      const firstFalseIndex = sortedSteps?.findIndex((step) => !step?.active);
      // if all steps are active
      if (
        firstFalseIndex === -1 ||
        (!_.isEmpty(sortedSteps[firstFalseIndex]) &&
          sortedSteps[firstFalseIndex]?.key === "done")
      ) {
        await dispatch(setOnboardingComplete(true));
        return setShowOnboarding(false);
      }
      await dispatch(setOnboardingComplete(false));
      setCurrentStep(firstFalseIndex + 1);
      setSortedSteps(sortedSteps);
      setShowOnboarding(true);
    }
  };

  const handleGetManagedNumbers = () => {
    let managedGraphData = [];
    const statesToIgnore = ["pending"];
    aggregationsIsManaged?.forEach((agg) => {
      let name = formatIacStatus(agg?.key);
      if (!statesToIgnore?.includes(agg?.key)) {
        managedGraphData.push({ key: name, doc_count: agg?.doc_count });
      }
    });
    setManagedGraphData(managedGraphData);
    const totalCount = _.reduce(
      managedGraphData,
      (prev, curr) => {
        return prev?.doc_count ? prev?.doc_count : prev + curr?.doc_count;
      },
      0
    );
    setTotalAssetsCount(totalCount);
    const graphFormattedData = _.map(managedGraphData, (agg) => {
      return {
        type: agg?.key,
        value: agg?.doc_count,
      };
    });
    setFormattedGraphData(graphFormattedData);
    setShowManagedGraph(true);
  };

  const getNumbersForCounters = () => {
    let output = [];
    if (!_.isEmpty(aggregationsAssetsVolume)) {
      output = aggregationsAssetsVolume?.[0]?.state?.buckets;
    }
    return setDatesData(output);
  };

  const handleArrangeRegion = () => {
    let match = aggregationsRegion?.find((agg) => agg.key === "aws");
    if (!_.isUndefined(match)) {
      setRegionsData(match.regions?.buckets);
    }
    setShowRegions(true);
  };

  const handleArrangeClassifications = () => {
    let categories = { Optimization: 0, Misconfiguration: 0, Reliability: 0 };
    _.map(aggregationsClassificaition, (accountClassification) => {
      let match = classifications?.find(
        (dbClassification) => dbClassification.id === accountClassification.key
      );
      let labels = match ? match.labels : [];
      _.map(labels, (label) => {
        if (Object.keys(categories)?.includes(label)) {
          categories[label] += 1;
        }
      });
    });
    setInsightsData(categories);
  };

  const fetchInitData = async () => {
    setInitLoading(true);
    await getAllData();
    setInitLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    await getAllData();
    setLoading(false);
  };

  const getAllData = async () => {
    const fields = "accountId,isProd"
    await Promise.all([
      dispatch(getAccountIntegrations(fields)),
      dispatch(
        getDashboardBaseAggregations(
          emptyInventoryScreenFilters,
          "",
          false,
          onlyProd
        )
      ),
      dispatch(getDashboardFireflyAggregations(onlyProd)),
      dispatch(getClassifications()),
      dispatch(getDashboardInsights(onlyProd)),
      dispatch(getIacStacksBaseAggs(emptyIacScreenFilters, "", onlyProd, true)),
      dispatch(getTotalCost(onlyProd)),
      dispatch(getAccountTotalAssets(onlyProd)),
    ]);
  };

  const handleOpenMissingIntegrations = async (data) => {
    await setMissingIntegrationsModalData(data);
    setMissingIntegrationsModalOpen(true);
  };

  const renderStatus = () => {
    const percentage = ((currentStep - 1) / (sortedSteps?.length - 1)) * 100;
    const match = sortedSteps[currentStep - 1];
    return (
      <div className="Dashboard__onBoarding-card-profile-status col">
        <span className="Dashboard__onBoarding-card-profile-status-header bold">{`Your onboarding is ${percentage}% complete`}</span>
        <span>{`To do: Step ${currentStep} | ${match?.text}`}</span>
      </div>
    );
  };

  const renderIntegrations = () => {
    let cloudProviders = {
      aws: { count: 0, name: "AWS Accounts"},
      k8s: { count: 0, name: "Kubernetes Clusters"},
      gcp: { count: 0, name: "Google Cloud Projects"},
      azurerm: { count: 0, name: "Azure Subscriptions"},
      saas: { count: 0, name: "SaaS Integrations"}
    };

    const awsAggregation = providerIdByProviderType?.find(
      (agg) => agg?.key === CLOUD_PROVIDERS.aws
    );
    const awsIntegrationIdsAgg = awsAggregation?.integrationId?.buckets || [];

    const k8sAggregation = providerIdByProviderType?.find(
      (agg) => agg?.key === CLOUD_PROVIDERS.k8s
    );
    const k8sIntegrationIdsAgg = k8sAggregation?.integrationId?.buckets || [];
    const gcpAggregation = providerIdByProviderType?.find(
      (agg) => agg?.key === CLOUD_PROVIDERS.gcp
    );
    const gcpIntegrationIdsAgg = gcpAggregation?.integrationId?.buckets || [];
    const azureAggregation = providerIdByProviderType?.find(
      (agg) => agg?.key === CLOUD_PROVIDERS.azurerm
    );
    const azureIntegrationIdsAgg = azureAggregation?.integrationId?.buckets || [];

    const saasIntegrationIdsAgg = providerIdByProviderType.reduce((acc, currAgg = {}) => {
      const isSaas = ![CLOUD_PROVIDERS.aws, CLOUD_PROVIDERS.k8s, CLOUD_PROVIDERS.gcp, CLOUD_PROVIDERS.azurerm].includes(currAgg.key);
      return isSaas ? acc + currAgg.integrationId?.buckets?.length || 0 : acc
    }, 0);

    cloudProviders.aws.count = awsIntegrationIdsAgg?.length || 0;
    cloudProviders.k8s.count = k8sIntegrationIdsAgg?.length || 0;
    cloudProviders.gcp.count = gcpIntegrationIdsAgg?.length || 0;
    cloudProviders.azurerm.count = azureIntegrationIdsAgg?.length || 0;
    cloudProviders.saas.count = saasIntegrationIdsAgg || 0;

    return _.map(Object.keys(cloudProviders), (provider, index) => {
      const isSaas = provider === "saas" && cloudProviders.saas.count > 0;
      const missingIntegrationsValue = getMissingIntegrationsValue(provider, aggregationsMissingAccountNumbers, excludeMissingIntegrations, isSaas);
      return (
        <Integration
          key={provider + index}
          provider={provider}
          count={cloudProviders[provider]?.count}
          name={cloudProviders[provider]?.name}
          missingIntegrations={missingIntegrationsValue}
          onlyProd={onlyProd}
          prodAccounts={prodAccountNumbers}
          openMissingIntegrations={() =>
            handleOpenMissingIntegrations({
              provider,
              count: cloudProviders[provider]?.count,
              missingIntegrations: missingIntegrationsValue
            })
          }
        />
      );
    });
  };

  const renderStates = () => {
    return _.map(Object.keys(statesData), (state, index) => {
      const match = _.find(
        managedGraphData,
        (graphObj) => graphObj?.key === state
      );
      return (
        <State
          key={state || "" + index}
          name={state}
          percentage={statesData[state]?.percentage}
          datesData={datesData}
          total={match ? match?.doc_count : 0}
          onlyProd={onlyProd}
          prodAccounts={prodAccountNumbers}
        />
      );
    });
  };

  const renderTypes = () => {
    if (!_.isEmpty(aggregationsStateTypeStatus)) {
      return _.map(iacTypes, (type) => {
        const statusAgg = aggregationsStateTypeStatus?.find(
          (iacTypeAgg) => iacTypeAgg?.key === type
        ) || { key: type, doc_count: 0 };
        const iacTypeStatuses = statusAgg?.statuses?.buckets;
        const outOfSync =
          iacTypeStatuses?.find((status) => status?.key === "outOfSync")
            ?.doc_count || 0;
        const partialSync =
          iacTypeStatuses?.find((status) => status?.key === "partialSync")
            ?.doc_count || 0;
        const corrupted =
          iacTypeStatuses?.find((status) => status?.key === "corrupted")
            ?.doc_count || 0;
        const syncIssues = outOfSync + partialSync + corrupted;
        return (
          <IacType
            key={uuidv4()}
            type={statusAgg}
            syncIssues={syncIssues || 0}
            onlyProd={onlyProd}
            prodAccounts={prodAccountNumbers}
          />
        );
      });
    }
    return (
      <div className="basic-card center" style={{ height: "100%" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" />
      </div>
    );
  };

  const renderInsights = () => {
    if (!_.isEmpty(insightsData)) {
      return _.map(Object.keys(insightsData), (category, index) => {
        return (
          <Insight
            key={category + index}
            category={category}
            count={insightsData[category]}
            cost={toLower(category) === "finops" ? totalCost : 0}
            onlyProd={onlyProd}
            prodAccounts={prodAccountNumbers}
          />
        );
      });
    }
  };

  const handleSetProdAccount = () => {
    if (!isEmpty(accountIntegrations)) {
      // arrange all the data to be a flat array of objects
      let allProviders = _.map(_.keys(accountIntegrations), (providerKey) => {
        return _.map(accountIntegrations?.[providerKey] || [], (integ) => {
          return {
            ...integ,
            provider: providerKey,
          };
        });
      });
      allProviders = _.flatten(allProviders);

      // filter data to find only "isProd" objects
      const prodAccounts = _.filter(allProviders, (integ) => integ?.isProd);

      // get only account numbers or ClusterId for K8S obj
      const prodAccountNumbers = _.map(prodAccounts || [], (item) => {
        return item?.accountNumber || item?.clusterId;
      });
      return dispatch(setProdAccounts(prodAccountNumbers));
    }
  };

  if (initLoading) {
    return (
      <div className="basic-page center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="Dashboard col">
      {showOnboarding && (
        <div className="Dashboard__onBoarding col">
          <span className="muted">Onboarding Status</span>
          <div className="Dashboard__onBoarding-card basic-card row between">
            <div className="Dashboard__onBoarding-card-profile row">
              <Progress
                type="circle"
                strokeColor={{
                  "0%": "#7F6AF4",
                  "100%": "#88E3E4",
                }}
                percent={((currentStep - 1) / (sortedSteps?.length - 1)) * 100}
                width={65}
                trailColor="#ffffff"
              />
              <div className="Dashboard__onBoarding-card-profile-img">
                <div
                  className="Dashboard__onBoarding-card-profile-img-inner"
                  style={{ backgroundImage: `url(${user?.picture})` }}
                />
              </div>
              {renderStatus()}
            </div>
            <ProgressBar
              steps={sortedSteps}
              currentStep={currentStep}
              customMargin="0 10%"
              customWidth={25}
              customStyle
            />
          </div>
        </div>
      )}
      <ProdToggle
        value={onlyProd}
        setStoreValue={(val) => dispatch(setOnlyProd(val))}
        disabled={isEmpty(prodAccountNumbers)}
      />
      {loading ? (
        <div className="basic-page center">
          <Spin />
        </div>
      ) : (
        <div className="Dashboard col">
          <div className="Dashboard__top">{renderIntegrations()}</div>
          <div className="Dashboard__center">
            <div className="Dashboard__center-container col">
                <span className="muted">Cloud Waste</span>
                <CloudWorkflows totalCost={totalCost} />
            </div>
            <div className="Dashboard__center-container col">
              <span className="muted">Assets</span>
              <div className="Dashboard__center-container-assets no-flex-card">
                {showManagedGraph && (
                  <div className="Dashboard__center-container-assets-graph col center">
                    <PieChart
                      data={formattedGraphData}
                      totalAssetsCount={totalAssetsCount}
                      datesData={datesData}
                    />
                    <div className="row center Dashboard__center-container-assets-graph-total row">
                      <div className="col center Dashboard__center-container-assets-graph-total-block col">
                        <span className="Dashboard__center-container-assets-graph-total-block-number">{numberWithCommas(accountTotals?.notExcluded || 0)}</span>
                        <span className="Dashboard__center-container-assets-graph-total-block-label">Inventory Assets</span>
                      </div>
                      <div className="Dashboard__center-container-assets-graph-total-seperator" />
                      <div className="col center Dashboard__center-container-assets-graph-total-block col">
                        <span className="Dashboard__center-container-assets-graph-total-block-number">{numberWithCommas(accountTotals?.excluded || 0)}</span>
                        <span className="Dashboard__center-container-assets-graph-total-block-label">Excluded Assets</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="Dashboard__center-container-assets center">
                  {showManagedGraph && (
                    <div className="Dashboard__center-container-assets-states col">
                      {renderStates()}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="Dashboard__center-container col">
              <span className="muted">IaC Stacks</span>
              <div className="Dashboard__center-container-iac">
                {renderTypes()}
              </div>
            </div>
          </div>
          <div className="Dashboard__centerBottom">
            <div className="Dashboard__centerBottom-container col">
              <span className="muted">Top 5 Drifted Properties</span>
              <TopFiveProperties />
            </div>
            <div className="Dashboard__centerBottom-container col">
              <span className="muted">Top 5 Unmanaged Resources</span>
              <Top5UnmanagedResources
                onlyProd={onlyProd}
                prodAccounts={prodAccountNumbers}
              />
            </div>
          </div>
          <div className="Dashboard__bottom col">
            <span className="muted">New policy violation by category</span>
            <div className="Dashboard__bottom-insights">{renderInsights()}</div>
          </div>
          <MissingIntegrationsModal
            visible={missingIntegrationsModalOpen}
            handleClose={() => {
              setMissingIntegrationsModalData(null);
              setMissingIntegrationsModalOpen(false);
            }}
            data={missingIntegrationsModalData}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
